import { useEffect, useState, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { getRelativePosition } from 'chart.js/helpers';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ChartOptions,
  ChartEvent,
  ActiveElement,
  Chart,
  ChartDataset,
} from 'chart.js';
import { IWeeklyTransaction } from '../../../interface/transaction';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { formatter, getWeekDateRange } from '../../../constants/utilityFunctions';

ChartJS.register(CategoryScale, LinearScale, BarElement);
interface Props {
  formatedWeeklyTransaction: IWeeklyTransaction[];
  selectedWeek: string | null;
  setSelectedWeek: (d: string) => void;
  setDateRange: (range: { startDate: string; endDate: string }) => void;
  handleClickedWeek: (week: string) => void;
}

const WeeklyBudgetGraph = ({
  formatedWeeklyTransaction,
  selectedWeek,
  setSelectedWeek,
  setDateRange,
  handleClickedWeek,
}: Props) => {
  const { t } = useTranslation();
  const chartRef = useRef<Chart<'bar'> | null>(null);
  const [subtitleColor, setSubtitleColor] = useState('#3F4150');
  const [primaryColor, setPrimaryColor] = useState('#0090A6');
  const [borderGrey, setBorderGrey] = useState('#E3E3E3');
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);

  useEffect(() => {
    const updateColors = () => {
      const style = getComputedStyle(document.documentElement);
      setSubtitleColor(style.getPropertyValue('--clr-subtitle').trim() || '#3F4150');
      setPrimaryColor(style.getPropertyValue('--clr-general-primary').trim() || '#0090A6');
      setBorderGrey(style.getPropertyValue('--clr-border-grey-100').trim() || '#E3E3E3');
    };

    updateColors();
    const observer = new MutationObserver(() => {
      updateColors();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-theme-color', 'class'],
    });

    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (selectedWeek && formatedWeeklyTransaction.length > 0) {
      const index = formatedWeeklyTransaction.findIndex(
        (transaction) => transaction.formatedWeek === selectedWeek,
      );
      setClickedIndex(index >= 0 ? index : null);
    } else {
      setClickedIndex(null);
    }
  }, [selectedWeek, formatedWeeklyTransaction]);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const handleCanvasClick = (event: MouseEvent) => {
      const canvasPosition = getRelativePosition(event, chart);
      const xAxis = chart.scales.x;

      // Only check clicks near the x-axis label area (below the bars)
      if (canvasPosition.y > xAxis.top && canvasPosition.y < xAxis.bottom + 20) {
        // Find which tick/label was clicked
        const ticks = xAxis.ticks;
        for (let i = 0; i < ticks.length; i++) {
          const tickX = xAxis.getPixelForTick(i);
          if (Math.abs(canvasPosition.x - tickX) < 20) {
            handleWeekClick(i);
            break;
          }
        }
      }
    };

    const canvas = chart.canvas;
    canvas.addEventListener('click', handleCanvasClick);

    return () => {
      canvas.removeEventListener('click', handleCanvasClick);
    };
  }, [
    chartRef,
    formatedWeeklyTransaction,
    selectedWeek,
    setSelectedWeek,
    setDateRange,
    handleClickedWeek,
  ]);

  const labels = formatedWeeklyTransaction.map((w) => w.formatedWeek);
  const billableData = formatedWeeklyTransaction.map((w) => w.billableHours);
  const budgetedData = formatedWeeklyTransaction.map((w) => w.budgetedHours);

  const billableMax = billableData.length > 0 ? Math.max(...billableData) : 0;
  const budgetedMax = budgetedData.length > 0 ? Math.max(...budgetedData) : 0;
  let maxValue = 40;
  if (billableMax || budgetedMax) {
    maxValue = Math.max(billableMax, budgetedMax);
  }

  const roundedMaxHours = Math.ceil(maxValue / 10) * 10;
  let yStepSize: number;
  if (maxValue < 10) {
    yStepSize = 2.5;
  } else if (maxValue > 30) {
    yStepSize = 10;
  } else {
    yStepSize = 5;
  }
  const handleWeekClick = (idx: number) => {
    const clickedWeek = formatedWeeklyTransaction[idx];
    if (!clickedWeek) return;

    if (clickedWeek.formatedWeek === selectedWeek) {
      setSelectedWeek('');

      setDateRange({
        startDate: dayjs().subtract(9, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
      });
      setClickedIndex(null);
    } else {
      setClickedIndex(idx);
      setSelectedWeek(clickedWeek.formatedWeek);
      const { weekStart, weekEnd } = getWeekDateRange(clickedWeek.year, clickedWeek.weekNumber);
      setDateRange({
        startDate: weekStart,
        endDate: weekEnd,
      });
      handleClickedWeek(clickedWeek.formatedWeek);
    }
  };

  const data = {
    labels,
    datasets: [
      {
        label: t('transaction:dashboardActualHours'),
        data: billableData,
        backgroundColor: primaryColor,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 7,
      },
      {
        label: t('transaction:dashboardBudgetHours'),
        data: budgetedData,
        backgroundColor: borderGrey,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 7,
      },
    ] as ChartDataset<'bar'>[],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: roundedMaxHours,
        grid: {
          color: borderGrey,
          drawTicks: false,
        },
        border: {
          display: false,
          dash: [3, 3],
        },
        ticks: {
          stepSize: yStepSize,
          callback: (value) =>
            `${formatter.regionalNumberFormat({ value: Number(value) })}${t('common:h')}`,
          color: subtitleColor,
          font: {
            size: 10,
            weight: 500,
          },
        },
      },
      x: {
        stacked: false,

        grid: {
          display: false,
          drawTicks: false,
        },
        border: {
          display: false,
        },
        ticks: {
          color: (ctx) => (ctx.index === clickedIndex ? '#E74C3C' : subtitleColor),
          font: {
            size: 10,
            weight: 500,
          },
        },
      },
    },
    onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
      if (!elements.length) return;
      handleWeekClick(elements[0].index);
    },
  };
  return (
    <div className="weekly-graph-container">
      <Bar ref={chartRef} data={data} options={options} />
    </div>
  );
};
export default WeeklyBudgetGraph;
